import { FC } from 'react';
import * as React from 'react';

import { FormRenderProps } from 'react-final-form';
import { ModalComponentProps } from '../modal/ModalManager';
import { Yup } from '../validation/yup';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { Card } from '../card/Card';
import { Block } from '../block/Block';
import { Col, Row } from '../row';
import { OnChange } from 'react-final-form-listeners';
import {
  Button,
  CloseButton,
  DialogFooter,
  Icon,
  makeValidate,
  modalFormDialog,
  VF,
} from '../index';
import { mcLock, mcUnlock } from '@mc/react-ui-icons';

export type ImageDialogFormData = {
  url: string;
  title?: string;
  width?: number;
  height?: number;
  imgSrc?: string; // base64data
  keepRatio?: boolean;
};

async function arrayBufferToBase64(buffer: ArrayBuffer) {
  // const blob = new Blob([buffer], { type: mimeType });
  const blob = new Blob([buffer]);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result); // bude to "data:image/png;base64,...."
    };

    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

type ImageDialogProps = {
  formRenderProps: FormRenderProps<ImageDialogFormData>;
  createMode: boolean;
} & ModalComponentProps;

const getImageDetails = async (
  url: string,
  fetchData: boolean
): Promise<{ width?: number; height?: number; imgSrc?: string }> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve({});
      return;
    }

    try {
      if (!url) throw new Error('URL není definována');
      new URL(url); // Ověření validity URL
    } catch {
      resolve({});
    }

    const img = new Image();
    img.crossOrigin = 'anonymous'; // Pro zajištění přístupu k byte datům

    img.onload = async () => {
      const width = img.width;
      const height = img.height;

      try {
        const response = await fetch(url);
        const byteData = fetchData
          ? await arrayBufferToBase64(await response.arrayBuffer())
          : undefined;
        // @ts-ignore
        resolve({ width, height, imgSrc: byteData });
      } catch {
        resolve({});
      }
    };

    img.onerror = () => {
      resolve({});
    };

    img.src = url;
  });
};

export const editZadostValidateSchema = Yup.object().shape({});
export const createZadostValidateSchema = Yup.object().shape({
  url: Yup.string().required(),
});
const validationCreate = makeValidate(createZadostValidateSchema);
const validationEdit = makeValidate(editZadostValidateSchema);
const validation = async (
  values: ImageDialogFormData,
  props: ImageDialogProps
) => {
  let errors = props.createMode
    ? await validationCreate(values)
    : await validationEdit(values);

  if (values.url && !values.imgSrc) {
    errors = {
      ...errors,
      url: 'Obrázek se nepodařilo načíst. Zkontrolujte URL.',
    };
  }

  return errors;
};

type OriginalImageSize = {
  width?: number;
  height?: number;
};

const ImageDialog: FC<ImageDialogProps> = ({
  createMode,
  onClose,
  formRenderProps: { handleSubmit, submitting, values, form, initialValues },
}) => {
  const [originalImageSize, setOriginalImageSize] =
    React.useState<OriginalImageSize>({
      width: initialValues?.width,
      height: initialValues?.height,
    });

  const handleUrlChange = async (url: string) => {
    if (!url) {
      form.change('width', undefined);
      form.change('height', undefined);
      return;
    }

    const imageDetails = await getImageDetails(url, true);
    if (imageDetails.imgSrc) {
      form.change('width', imageDetails.width);
      form.change('height', imageDetails.height);
      form.change('imgSrc', imageDetails.imgSrc);

      setOriginalImageSize({
        width: imageDetails.width,
        height: imageDetails.height,
      });
    } else {
      form.change('width', undefined);
      form.change('height', undefined);
      form.change('imgSrc', undefined);

      setOriginalImageSize({
        width: undefined,
        height: undefined,
      });
    }
  };

  const dimensionChangeDisabled = submitting || (createMode && !values.imgSrc);

  return (
    <Dialog size={'md'} onClose={onClose} title={'Obrázek'}>
      <DialogBody>
        <Card mode={'edit'}>
          <Block>
            {createMode && <VF.String required name={'url'} label={'URL'} />}
            {createMode && <OnChange name={'url'}>{handleUrlChange}</OnChange>}
            <VF.String name={'title'} label={'Popis obrázku'} />
            <Row noGutters>
              <Col xs={2}>
                <VF.Number
                  disabled={dimensionChangeDisabled}
                  noThousandsSeparator
                  name={'width'}
                  label={'Šířka'}
                  onChange={(e) => {
                    const value = e.target.value;
                    const originalWidth = originalImageSize.width;
                    const originalHeight = originalImageSize.height;

                    if (values.keepRatio && originalWidth && originalHeight) {
                      const ratio = originalWidth / originalHeight;
                      const newHeight = Math.round(value / ratio);
                      form.change('height', newHeight);
                    }
                    form.change('width', value);
                  }}
                />
              </Col>
              <Col xs={'auto'} className={'px-1'}>
                <VF.DetailComponent
                  emptyLabel
                  value={
                    <Button
                      disabled={dimensionChangeDisabled}
                      style={'link'}
                      onClick={() => {
                        form.change('keepRatio', !values.keepRatio);
                      }}
                      icon={
                        <Icon
                          fixedWidth
                          icon={values.keepRatio ? mcLock : mcUnlock}
                          title={'Zachovat poměr stran'}
                        />
                      }
                    />
                  }
                />
              </Col>
              <Col xs={2}>
                <VF.Number
                  disabled={dimensionChangeDisabled}
                  noThousandsSeparator
                  name={'height'}
                  label={'Výška'}
                  onChange={(e) => {
                    const value = e.target.value;
                    const originalWidth = originalImageSize.width;
                    const originalHeight = originalImageSize.height;

                    if (values.keepRatio && originalWidth && originalHeight) {
                      const ratio = originalHeight / originalWidth;
                      const newWidth = Math.round(value / ratio);
                      form.change('width', newWidth);
                    }
                    form.change('height', value);
                  }}
                />
              </Col>
            </Row>
          </Block>
        </Card>
      </DialogBody>
      <DialogFooter>
        <CloseButton mode={'edit'} disabled={submitting} onClick={onClose} />
        <Button disabled={submitting} style={'primary'} onClick={handleSubmit}>
          Ok
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export const ImageModalForm = modalFormDialog<
  ImageDialogFormData,
  ImageDialogProps
>(ImageDialog, {
  validate: validation,
});
